import { useEffect, useState } from "react";

import ClientsDashboardServices from "../../../../services/ClientsDashboardServices";
import {
  UseGenerateDataProps,
  UseGenerateDataReturn,
  UseGenerateDataError,
  Data,
  PreviousFilter,
} from "./useGenerateData.types";
import { TABPARAMNAME } from "../../../../constants/clientsDashboard";
import { TabParamName } from "../clients-dashboard-types";

const defaultError: UseGenerateDataError = {
  showError: false,
  simulations: "",
  hirings: "",
  insurancePremiumPortfolio: "",
};

const useGenerateData = ({
  filter,
  userTabsKeys,
}: UseGenerateDataProps): UseGenerateDataReturn => {
  const [data, setData] = useState<Data | null>(null);
  const [previousFilter, setPreviousFilter] = useState<PreviousFilter>({
    from: "",
    to: "",
    mediatorCode: "",
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<UseGenerateDataError>(defaultError);

  useEffect(() => {
    if (previousFilter.mediatorCode !== filter.mediatorCode) {
      setData(null);
    }
  }, [filter.mediatorCode]);

  const handleOnGenerate = async () => {
    setLoading(true);

    const commonParams = {
      loader: false,
      setLoading: () => {},
      from: filter.from.replace("/", "-"),
      to: filter.to.replace("/", "-"),
      mediatorCode: filter.mediatorCode,
    };

    try {
      const [simResponse, hiringResponse, insurancePremiumPortfolioResponse] = await Promise.all([
        userTabsKeys.includes(TABPARAMNAME.SIMULACIONES as TabParamName)
          ? ClientsDashboardServices.getSimulations(commonParams)
          : Promise.resolve(null),
        userTabsKeys.includes(TABPARAMNAME.SOLICITUDES as TabParamName)
          ? ClientsDashboardServices.getHirings(commonParams)
          : Promise.resolve(null),
        userTabsKeys.includes(TABPARAMNAME.CARTERAS as TabParamName)
          ? ClientsDashboardServices.getInsurancePremiumPortfolio(commonParams)
          : Promise.resolve(null),
      ]);

      const newError = { ...defaultError };

      const newData: Data = {};

      if (simResponse) {
        if (simResponse.ok) {
          newData.simulaciones = simResponse.data;
        } else {
          newError.showError = true;
          newError.simulations = simResponse.message;
        }
      }

      if (hiringResponse) {
        if (hiringResponse.ok) {
          newData.solicitudes = hiringResponse.data;
        } else {
          newError.showError = true;
          newError.hirings = hiringResponse.message;
        }
      }

      if (insurancePremiumPortfolioResponse) {
        if (insurancePremiumPortfolioResponse.ok) {
          newData.carteras = insurancePremiumPortfolioResponse.data;
        } else {
          newError.showError = true;
          newError.insurancePremiumPortfolio = insurancePremiumPortfolioResponse.message;
        }
      }

      setData(newData);
      setError(newError);
    } catch (error) {
      const errorMsg = "Se ha producido un error, vuelva a intentarlo más tarde";

      setError({
        showError: true,
        simulations: errorMsg,
        hirings: errorMsg,
        insurancePremiumPortfolio: errorMsg,
      });
    } finally {
      setPreviousFilter(filter);
      setLoading(false);
    }
  };

  return {
    data,
    loading,
    error,
    setError,
    handleOnGenerate,
  };
};

export default useGenerateData;
